import React from 'react'
import { connect, useDispatch } from 'react-redux'
import { CSSTransition } from 'react-transition-group'

import {
  BuildingInterface,
  setByFlag as setByFlagApartment,
} from '@store/actionSlices/building'
import { setByFlag as setByFlagHouseAndLand } from '@store/actionSlices/houseAndLand'
import { RootStateTypeExtra } from '@store/types'

import IdleTimeHandler from '@components/idle-time-handler'

import { HouseAndLandActiveKeyType } from '@api/types/house-and-land-type'

import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import { PropertyType } from '@adUtilities/types'

import ComparablePropertyItem from './comparable-property-item'
import Controls from './controls'

interface ComparePropertyProps {
  comparePropertyProps: ReturnType<typeof useCompareProperty>
  showPropertyInfo: boolean
  onFullScreenToggle: (status: boolean) => void
  onPropertyInfoToggle: (status: boolean) => void
  hideControls?: boolean
  disableIdleTimeHandler?: boolean
  levels: BuildingInterface['levels']
}

function CompareProperty({
  comparePropertyProps,
  showPropertyInfo,
  onFullScreenToggle,
  onPropertyInfoToggle,
  hideControls,
  disableIdleTimeHandler,
  levels,
}: ComparePropertyProps) {
  const dispatch = useDispatch()
  const {
    compareModeIsActive,
    comparableProperties,
    initialPropertyAtComparisonStart,
    stopComparison,
  } = comparePropertyProps
  const [isTransitioning, setIsTransitioning] = React.useState<boolean>(false)

  const handleTransitionState = React.useCallback(
    (state: boolean) => () => {
      setIsTransitioning(state)
    },
    []
  )

  const setLastActiveUnit = React.useCallback(() => {
    if (initialPropertyAtComparisonStart?.propertyType !== PropertyType.Unit) {
      return
    }
    const flattenedLevels = levels.flatMap((item) => item.data)
    const blockId = flattenedLevels.find(
      (item) => item.name === initialPropertyAtComparisonStart.data.name
    )?.blockId

    if (!blockId) {
      return
    }

    dispatch(
      setByFlagApartment({
        flag: 'activeBlock',
        value: blockId,
      })
    )
    dispatch(
      setByFlagApartment({
        flag: 'activeLevel',
        value: initialPropertyAtComparisonStart.references.levelId,
      })
    )
    dispatch(
      setByFlagApartment({
        flag: 'activeUnit',
        value: initialPropertyAtComparisonStart.data.name,
      })
    )
  }, [initialPropertyAtComparisonStart, levels])

  const setLastActiveLot = React.useCallback(() => {
    if (initialPropertyAtComparisonStart?.propertyType !== PropertyType.Lot) {
      return
    }

    dispatch(
      setByFlagHouseAndLand({
        flag: HouseAndLandActiveKeyType.ActivePrecinctId,
        value: initialPropertyAtComparisonStart.references.precinctId,
      })
    )
    dispatch(
      setByFlagHouseAndLand({
        flag: HouseAndLandActiveKeyType.ActiveStageId,
        value: initialPropertyAtComparisonStart.references.stageId,
      })
    )
    dispatch(
      setByFlagHouseAndLand({
        flag: HouseAndLandActiveKeyType.ActiveLotId,
        value: initialPropertyAtComparisonStart.data.id,
      })
    )
    dispatch(
      setByFlagHouseAndLand({
        flag: HouseAndLandActiveKeyType.ActiveLotName,
        value: initialPropertyAtComparisonStart.data.name,
      })
    )
  }, [initialPropertyAtComparisonStart])

  const handleStopComparison = React.useCallback(() => {
    stopComparison()
    if (initialPropertyAtComparisonStart?.propertyType === PropertyType.Unit) {
      setLastActiveUnit()
    }
    if (initialPropertyAtComparisonStart?.propertyType === PropertyType.Lot) {
      setLastActiveLot()
    }
  }, [
    stopComparison,
    setLastActiveUnit,
    setLastActiveLot,
    initialPropertyAtComparisonStart?.propertyType,
  ])

  if (!compareModeIsActive) {
    return null
  }

  return (
    <div className="relative flex h-full w-full">
      <IdleTimeHandler disable={disableIdleTimeHandler}>
        {hideControls ? null : (
          <div className="absolute top-0 z-5 w-full items-start p-4">
            <Controls
              showPropertyInfo={showPropertyInfo}
              onFullScreenToggle={onFullScreenToggle}
              onPropertyInfoToggle={onPropertyInfoToggle}
              onStopComparison={handleStopComparison}
            />
          </div>
        )}
      </IdleTimeHandler>
      <div className="flex w-full bg-white">
        {comparableProperties.map((item) => (
          <CSSTransition
            key={item.index}
            classNames={`basis-1/2 ${
              item.index === 0
                ? 'transition-property-compare-first-item'
                : 'transition-property-compare-last-item'
            }`}
            timeout={500}
            in
            appear
            mountOnEnter
            unmountOnExit
            onEnter={handleTransitionState(true)}
            onEntered={handleTransitionState(false)}
            onExit={handleTransitionState(true)}
            onExited={handleTransitionState(false)}
          >
            <div className="h-full">
              <CSSTransition
                key={`item_${item.index}`}
                classNames={
                  isTransitioning ? '' : 'transition-property-compare-select'
                }
                in={!!item.property}
                timeout={300}
              >
                <div
                  className={`h-full w-full ${
                    isTransitioning ? 'opacity-100' : ''
                  }`}
                >
                  <ComparablePropertyItem
                    comparableProperty={item}
                    showPropertyInfo={showPropertyInfo}
                  />
                </div>
              </CSSTransition>
            </div>
          </CSSTransition>
        ))}
      </div>
    </div>
  )
}

export default connect(({ building: { levels } }: RootStateTypeExtra) => ({
  levels,
}))(CompareProperty)
